import { useRef, useState } from 'react'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Textarea } from '@journ/ui/textarea'
import { useIntlayer } from 'react-intlayer'
import type { Message } from '../domain/chat'

type MessageComposerProps = {
	onSend: (text: string) => Promise<unknown>
	replyTo?: Message | null
	onCancelReply?: () => void
}

const bodyText = (content: Record<string, unknown>): string => (typeof content.txt === 'string' ? content.txt : '')

const MessageComposer = ({ onSend, replyTo, onCancelReply }: MessageComposerProps) => {
	const content = useIntlayer('chat')
	const [text, setText] = useState('')
	const [sending, setSending] = useState(false)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	const submit = async (): Promise<void> => {
		const value = text.trim()
		if (!value || sending) return
		setSending(true)
		const result = await onSend(value)
		setSending(false)
		if (result) setText('')
		textareaRef.current?.focus()
	}

	return (
		<div className='border-t border-border bg-background px-4 py-3'>
			<form
				onSubmit={e => {
					e.preventDefault()
					submit()
				}}
				className='flex items-end gap-2 border border-border bg-muted/40 px-3 py-2 transition-shadow focus-within:ring-1 focus-within:ring-ring'
			>
				<div className='flex min-h-8 flex-1 flex-col'>
					{replyTo && (
						<div className='mb-2 flex items-start gap-2 rounded-sm border-l-2 border-primary/40 bg-muted/50 px-2 py-1.5 text-xs'>
							<div className='flex min-w-0 flex-1 flex-col'>
								<span className='font-medium text-foreground'>{replyTo.fromName}</span>
								<span className='truncate text-muted-foreground'>{bodyText(replyTo.content)}</span>
							</div>
							<Button
								type='button'
								variant='ghost'
								size='icon'
								className='h-5 w-5 shrink-0'
								onClick={onCancelReply}
								aria-label={content.cancelReply.value}
							>
								<Icons.Close size={12} />
							</Button>
						</div>
					)}
					<Textarea
						ref={textareaRef}
						value={text}
						onChange={e => setText(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault()
								submit()
							}
						}}
						placeholder={content.messagePlaceholder.value}
						rows={1}
						className='max-h-32 min-h-8 flex-1 resize-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0'
					/>
				</div>
				<Button
					type='submit'
					size='icon'
					disabled={!text.trim() || sending}
					className='h-8 w-8 shrink-0'
				>
					<Icons.ArrowUpward size={16} />
				</Button>
			</form>
		</div>
	)
}

export { MessageComposer }
